import React, { useState } from 'react';
import Papa from 'papaparse';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { bulkCreateInventory } from '../../../services/supabaseService';

const InventoryOnboardingWizard = ({ centerId, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [csvData, setCsvData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [quickAddForm, setQuickAddForm] = useState({
    unitNumber: '',
    floor: '',
    areaSqft: '',
    unitType: 'bareshell',
    coworkingSeatType: 'hot_desk',
    capacitySeats: '',
    pricePerSeat: '',
    pricePerSqft: '',
    fixedMonthlyPrice: '',
    status: 'vacant',
  });

  const csvTemplate = [
    ['unit_number', 'floor', 'area_sqft', 'unit_type', 'coworking_seat_type', 'capacity_seats', 'capacity_sqft', 'pricing_model', 'price_per_seat', 'price_per_sqft', 'fixed_monthly_price', 'status'],
    ['HD-101', '1', '400', 'coworking', 'hot_desk', '20', '400', 'per_seat', '3500', '0', '', 'vacant'],
    ['FD-201', '2', '500', 'coworking', 'fixed_desk', '25', '500', 'per_seat', '4500', '0', '', 'vacant'],
    ['BS-301', '3', '1000', 'bareshell', '', '', '1000', 'per_sqft', '', '100', '', 'vacant'],
    ['MO-401', '4', '1200', 'managed_office', '', '', '1200', 'per_sqft', '', '85', '', 'vacant'],
    ['FN-501', '5', '2000', 'furnished', '', '', '2000', 'fixed', '', '', '150000', 'vacant'],
  ];

  const handleDownloadTemplate = () => {
    const csv = Papa?.unparse(csvTemplate);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventory_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    Papa?.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        validateCSVData(results?.data);
      },
      error: (error) => {
        setErrors([{ row: 0, message: `CSV parse error: ${error?.message}` }]);
      },
    });
  };

  const validateCSVData = (data) => {
    const validationErrors = [];
    const validatedData = [];

    data?.forEach((row, index) => {
      const rowErrors = [];

      // Basic required fields
      if (!row?.unit_number) rowErrors?.push('unit_number is required');
      if (!row?.floor) rowErrors?.push('floor is required');
      if (!row?.area_sqft || isNaN(row?.area_sqft)) rowErrors?.push('area_sqft must be a number');
      if (!['coworking', 'managed_office', 'bareshell', 'furnished']?.includes(row?.unit_type)) {
        rowErrors?.push('unit_type must be one of: coworking, managed_office, bareshell, furnished');
      }
      if (!['vacant', 'occupied', 'under_maintenance', 'reserved']?.includes(row?.status)) {
        rowErrors?.push('status must be one of: vacant, occupied, under_maintenance, reserved');
      }

      // Validate pricing_model
      if (!['per_seat', 'per_sqft', 'fixed']?.includes(row?.pricing_model)) {
        rowErrors?.push('pricing_model must be one of: per_seat, per_sqft, fixed');
      }

      // Coworking-specific validation
      if (row?.unit_type === 'coworking') {
        if (!['hot_desk', 'fixed_desk', 'manager_cabin', 'team_suite_with_manager', 'team_suite_without_manager']?.includes(row?.coworking_seat_type)) {
          rowErrors?.push('coworking_seat_type is required for coworking units');
        }
        if (!row?.capacity_seats || isNaN(row?.capacity_seats) || parseInt(row?.capacity_seats) <= 0) {
          rowErrors?.push('capacity_seats must be a positive number for coworking units');
        }
        if (!row?.price_per_seat || isNaN(row?.price_per_seat) || parseFloat(row?.price_per_seat) <= 0) {
          rowErrors?.push('price_per_seat must be a positive number for coworking units');
        }
        if (row?.pricing_model !== 'per_seat') {
          rowErrors?.push('coworking units must use per_seat pricing_model');
        }
      }

      // Per-sqft pricing validation
      if (row?.pricing_model === 'per_sqft') {
        if (!row?.price_per_sqft || isNaN(row?.price_per_sqft) || parseFloat(row?.price_per_sqft) <= 0) {
          rowErrors?.push('price_per_sqft must be a positive number for per_sqft pricing');
        }
        if (!row?.capacity_sqft || isNaN(row?.capacity_sqft) || parseFloat(row?.capacity_sqft) <= 0) {
          rowErrors?.push('capacity_sqft must be a positive number for per_sqft pricing');
        }
      }

      // Fixed pricing validation
      if (row?.pricing_model === 'fixed') {
        if (!row?.fixed_monthly_price || isNaN(row?.fixed_monthly_price) || parseFloat(row?.fixed_monthly_price) <= 0) {
          rowErrors?.push('fixed_monthly_price must be a positive number for fixed pricing');
        }
      }

      if (rowErrors?.length > 0) {
        validationErrors?.push({ row: index + 2, message: rowErrors?.join(', ') });
      } else {
        // Build validated data object
        const validatedRow = {
          unitNumber: row?.unit_number,
          floor: row?.floor,
          areaSqft: parseFloat(row?.area_sqft),
          unitType: row?.unit_type,
          status: row?.status,
          pricingModel: row?.pricing_model,
        };

        // Add coworking-specific fields
        if (row?.unit_type === 'coworking') {
          validatedRow.coworkingSeatType = row?.coworking_seat_type;
          validatedRow.capacityType = 'seats';
          validatedRow.capacitySeats = parseInt(row?.capacity_seats);
          validatedRow.pricePerSeat = parseFloat(row?.price_per_seat);
          validatedRow.capacitySqft = parseFloat(row?.capacity_sqft || row?.area_sqft);
          validatedRow.totalPrice = parseInt(row?.capacity_seats) * parseFloat(row?.price_per_seat);
        } else if (row?.pricing_model === 'per_sqft') {
          validatedRow.capacityType = 'sqft';
          validatedRow.capacitySqft = parseFloat(row?.capacity_sqft || row?.area_sqft);
          validatedRow.pricePerSqft = parseFloat(row?.price_per_sqft);
          validatedRow.totalPrice = parseFloat(row?.capacity_sqft || row?.area_sqft) * parseFloat(row?.price_per_sqft);
        } else if (row?.pricing_model === 'fixed') {
          validatedRow.capacityType = 'sqft';
          validatedRow.capacitySqft = parseFloat(row?.capacity_sqft || row?.area_sqft);
          validatedRow.fixedMonthlyPrice = parseFloat(row?.fixed_monthly_price);
          validatedRow.totalPrice = parseFloat(row?.fixed_monthly_price);
        }

        validatedData?.push(validatedRow);
      }
    });

    setErrors(validationErrors);
    setCsvData(validatedData);

    if (validationErrors?.length === 0 && validatedData?.length > 0) {
      setStep(3);
    }
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      const result = await bulkCreateInventory(csvData, centerId);

      if (result?.success) {
        onSuccess?.();
        onClose?.();
      } else {
        setErrors([{ row: 0, message: result?.error || 'Import failed' }]);
      }
    } catch (error) {
      setErrors([{ row: 0, message: error?.message || 'Import failed' }]);
    } finally {
      setImporting(false);
    }
  };

  const handleQuickAddChange = (e) => {
    const { name, value } = e?.target;
    setQuickAddForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handler for Select components (receives value directly, not event)
  const handleSelectChange = (name) => (value) => {
    setQuickAddForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuickAddSubmit = async (e) => {
    e?.preventDefault();
    try {
      setImporting(true);
      
      // Build inventory data based on unit type and pricing model
      const inventoryData = {
        unitNumber: quickAddForm?.unitNumber,
        floor: quickAddForm?.floor,
        areaSqft: parseFloat(quickAddForm?.areaSqft),
        unitType: quickAddForm?.unitType,
        status: quickAddForm?.status,
      };

      // Add fields based on unit type
      if (quickAddForm?.unitType === 'coworking') {
        inventoryData.coworkingSeatType = quickAddForm?.coworkingSeatType;
        inventoryData.capacityType = 'seats';
        inventoryData.capacitySeats = parseInt(quickAddForm?.capacitySeats);
        inventoryData.capacitySqft = parseFloat(quickAddForm?.areaSqft);
        inventoryData.pricingModel = 'per_seat';
        inventoryData.pricePerSeat = parseFloat(quickAddForm?.pricePerSeat);
        inventoryData.totalPrice = parseInt(quickAddForm?.capacitySeats) * parseFloat(quickAddForm?.pricePerSeat);
      } else {
        // Non-coworking units
        inventoryData.capacityType = 'sqft';
        inventoryData.capacitySqft = parseFloat(quickAddForm?.areaSqft);
        
        // Determine pricing model based on form input
        if (quickAddForm?.fixedMonthlyPrice) {
          inventoryData.pricingModel = 'fixed';
          inventoryData.fixedMonthlyPrice = parseFloat(quickAddForm?.fixedMonthlyPrice);
          inventoryData.totalPrice = parseFloat(quickAddForm?.fixedMonthlyPrice);
        } else {
          inventoryData.pricingModel = 'per_sqft';
          inventoryData.pricePerSqft = parseFloat(quickAddForm?.pricePerSqft);
          inventoryData.totalPrice = parseFloat(quickAddForm?.areaSqft) * parseFloat(quickAddForm?.pricePerSqft);
        }
      }

      const result = await bulkCreateInventory([inventoryData], centerId);

      if (result?.success) {
        onSuccess?.();
        onClose?.();
      } else {
        alert(result?.error || 'Failed to add inventory unit');
      }
    } catch (error) {
      alert(error?.message || 'Failed to add inventory unit');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Inventory Onboarding</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="ml-2 font-medium">Download Template</span>
            </div>
            <div className={`w-16 h-1 mx-4 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Upload & Validate</span>
            </div>
            <div className={`w-16 h-1 mx-4 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span className="ml-2 font-medium">Import</span>
            </div>
          </div>

          {/* Step 1: Download Template */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Step A: Download CSV Template</h3>
                <p className="text-blue-700 mb-4">Download the template and fill in your inventory data. Required fields vary by unit type and pricing model.</p>
                <Button onClick={handleDownloadTemplate} className="flex items-center gap-2">
                  <Icon name="Download" size={20} />
                  Download Template
                </Button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-2">CSV Template Fields:</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                  <li><strong>unit_number</strong>: Unique unit identifier (required)</li>
                  <li><strong>floor</strong>: Floor number (required)</li>
                  <li><strong>area_sqft</strong>: Total area in square feet (required)</li>
                  <li><strong>unit_type</strong>: coworking, managed_office, bareshell, furnished (required)</li>
                  <li><strong>coworking_seat_type</strong>: hot_desk, fixed_desk, manager_cabin, team_suite_with_manager, team_suite_without_manager (required for coworking)</li>
                  <li><strong>capacity_seats</strong>: Number of seats (required for coworking)</li>
                  <li><strong>capacity_sqft</strong>: Usable sqft (required for non-coworking)</li>
                  <li><strong>pricing_model</strong>: per_seat, per_sqft, fixed (required)</li>
                  <li><strong>price_per_seat</strong>: Price per seat (required for per_seat pricing)</li>
                  <li><strong>price_per_sqft</strong>: Price per sqft (required for per_sqft pricing)</li>
                  <li><strong>fixed_monthly_price</strong>: Fixed monthly price (required for fixed pricing)</li>
                  <li><strong>status</strong>: vacant, occupied, under_maintenance, reserved (required)</li>
                </ul>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setStep(2)}>Next: Upload CSV</Button>
              </div>
            </div>
          )}

          {/* Step 2: Upload & Validate */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Step B: Upload CSV File</h3>
                <p className="text-blue-700 mb-4">Upload your filled CSV file. We'll validate the data and show any errors.</p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none"
                />
              </div>

              {errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} />
                    Validation Errors ({errors?.length})
                  </h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {errors?.map((error, index) => (
                      <div key={index} className="text-sm text-red-700">
                        <strong>Row {error?.row}:</strong> {error?.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button onClick={() => setStep(1)} variant="outline">Back</Button>
              </div>
            </div>
          )}

          {/* Step 3: Preview & Import */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <Icon name="CheckCircle" size={20} />
                  Step C: Ready to Import
                </h3>
                <p className="text-green-700">Validation successful! {csvData?.length} units ready to import.</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Number</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Floor</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Type</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pricing</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Price</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {csvData?.slice(0, 10)?.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">{row?.unitNumber}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{row?.floor}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{row?.unitType}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {row?.pricingModel === 'per_seat' && `${row?.capacitySeats} seats @ ₹${row?.pricePerSeat}`}
                            {row?.pricingModel === 'per_sqft' && `${row?.capacitySqft} sqft @ ₹${row?.pricePerSqft}`}
                            {row?.pricingModel === 'fixed' && `Fixed ₹${row?.fixedMonthlyPrice}`}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">₹{row?.totalPrice?.toLocaleString()}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{row?.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {csvData?.length > 10 && (
                  <div className="bg-gray-50 px-4 py-2 text-sm text-gray-600 text-center">
                    Showing 10 of {csvData?.length} units
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <Button onClick={() => setStep(2)} variant="outline">Back</Button>
                <Button onClick={handleImport} loading={importing} className="flex items-center gap-2">
                  <Icon name="Upload" size={20} />
                  Import {csvData?.length} Units
                </Button>
              </div>
            </div>
          )}

          {/* Quick Add Form */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Or Quick Add Single Unit</h3>
            <form onSubmit={handleQuickAddSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Unit Number"
                  name="unitNumber"
                  value={quickAddForm?.unitNumber}
                  onChange={handleQuickAddChange}
                  placeholder="A-101"
                  required
                />
                <Input
                  label="Floor"
                  name="floor"
                  value={quickAddForm?.floor}
                  onChange={handleQuickAddChange}
                  placeholder="1"
                  required
                />
                <Input
                  label="Area (sqft)"
                  name="areaSqft"
                  type="number"
                  value={quickAddForm?.areaSqft}
                  onChange={handleQuickAddChange}
                  placeholder="500"
                  required
                />
                <Select
                  label="Unit Type"
                  name="unitType"
                  value={quickAddForm?.unitType}
                  onChange={handleSelectChange('unitType')}
                  options={[
                    { value: 'coworking', label: 'Coworking Seat Blocks' },
                    { value: 'managed_office', label: 'Managed Office Blocks' },
                    { value: 'bareshell', label: 'Bareshell Sqft Blocks' },
                    { value: 'furnished', label: 'Furnished Office' },
                  ]}
                />

                {/* Coworking-specific fields */}
                {quickAddForm?.unitType === 'coworking' && (
                  <>
                    <Select
                      label="Coworking Seat Type"
                      name="coworkingSeatType"
                      value={quickAddForm?.coworkingSeatType}
                      onChange={handleSelectChange('coworkingSeatType')}
                      options={[
                        { value: 'hot_desk', label: 'Hot Desk' },
                        { value: 'fixed_desk', label: 'Fixed Desk' },
                        { value: 'manager_cabin', label: 'Manager Cabin' },
                        { value: 'team_suite_with_manager', label: 'Team Suite with Manager' },
                        { value: 'team_suite_without_manager', label: 'Team Suite without Manager' },
                      ]}
                    />
                    <Input
                      label="Number of Seats"
                      name="capacitySeats"
                      type="number"
                      value={quickAddForm?.capacitySeats}
                      onChange={handleQuickAddChange}
                      placeholder="20"
                      required
                    />
                    <Input
                      label="Price per Seat (₹)"
                      name="pricePerSeat"
                      type="number"
                      value={quickAddForm?.pricePerSeat}
                      onChange={handleQuickAddChange}
                      placeholder="3500"
                      required
                    />
                  </>
                )}

                {/* Non-coworking pricing fields */}
                {quickAddForm?.unitType !== 'coworking' && (
                  <>
                    <Input
                      label="Price per sqft (₹)"
                      name="pricePerSqft"
                      type="number"
                      value={quickAddForm?.pricePerSqft}
                      onChange={handleQuickAddChange}
                      placeholder="100"
                      required={!quickAddForm?.fixedMonthlyPrice}
                    />
                    <Input
                      label="Fixed Monthly Price (₹) - Optional"
                      name="fixedMonthlyPrice"
                      type="number"
                      value={quickAddForm?.fixedMonthlyPrice}
                      onChange={handleQuickAddChange}
                      placeholder="150000"
                    />
                  </>
                )}

                <Select
                  label="Status"
                  name="status"
                  value={quickAddForm?.status}
                  onChange={handleSelectChange('status')}
                  options={[
                    { value: 'vacant', label: 'Vacant' },
                    { value: 'occupied', label: 'Occupied' },
                    { value: 'under_maintenance', label: 'Under Maintenance' },
                    { value: 'reserved', label: 'Reserved' },
                  ]}
                />
              </div>
              <div className="flex justify-end">
                <Button type="submit" loading={importing} className="flex items-center gap-2">
                  <Icon name="Plus" size={20} />
                  Add Unit
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryOnboardingWizard;